// Provider-agnostic analytics facade.
//
// Call `analytics.init(...)` once at app boot, then `analytics.track(event, props)`
// from anywhere. Swap providers by changing VITE_ANALYTICS_PROVIDER — call sites
// don't change.
//
// Every payload runs through a PII allowlist filter before reaching the adapter.
// Any key not in ALLOWED_PROPERTY_KEYS is silently dropped, so a refactor that
// adds `serial_number` to a `track()` call cannot leak it.

import NoopAdapter from './analytics/NoopAdapter';

// Keys that may appear in any tracked event payload. Intentionally restrictive:
// metadata + low-risk classification fields only. No serial numbers, prices,
// names, addresses, or free-text notes.
const ALLOWED_PROPERTY_KEYS = new Set([
  'app_flow',
  'scan_session_id',
  'is_edit_mode',
  // scan shape
  'slot_count',
  'slot_breakdown',
  'file_sizes_kb',
  'file_mime_types',
  'duration_ms',
  'success',
  // error_code: snake_case slug naming the failure mode. Convention:
  //   - Backend codes describe WHAT failed (extraction, upstream, validation):
  //     "heic_not_supported", "extraction_failed", "quota_exceeded".
  //   - Frontend codes describe HOW it failed (transport / client-side):
  //     "network_error", "http_500", "no_items_extracted".
  // When debugging a funnel, filter by event name + success=false first,
  // then breakdown by error_code. Keep this comment in sync with the
  // matching block in services/analytics/__init__.py.
  'error_code',
  'items_extracted_count',
  'fields_extracted_count',
  // low-risk classification
  'item_category',
  'item_subcategory',
  'manufacturer',
  'color',
  // form / save
  'fields_populated',
  'fields_filled_count',
  'was_scan_assisted',
  'was_dirty',
  'ms_since_scan',
  'image_count_by_slot',
  'image_upload_success_count',
  'image_upload_fail_count',
  'dest',
  // image upload
  'slot_type',
  'size_kb',
  'mime_type',
  'ms_since_save',
  // field-edit signal
  'field_name',
  'was_autofilled',
  'was_changed',
  // house flows — form funnel + list/details navigation. Keep this block
  // in sync with the matching one in services/analytics/__init__.py.
  'tab_key',
  'was_placeholder',
  'address_was_autofilled',
  'validation_error_count',
  'house_count',
  // profile settings — generic on/off state for toggle events
  // (dark mode, notifications, etc.).
  'toggle_state',
]);

const filterProperties = (properties) => {
  if (!properties || typeof properties !== 'object') return {};
  const out = {};
  for (const key of Object.keys(properties)) {
    if (!ALLOWED_PROPERTY_KEYS.has(key)) continue;
    const value = properties[key];
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
};

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC4122 v4 fallback for older browsers / jsdom.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

class AnalyticsService {
  constructor() {
    this.adapter = NoopAdapter;
    this.initialized = false;
    this.sessionId = null;
  }

  async init(config = {}) {
    if (this.initialized) return;
    this.initialized = true;

    const provider = (config.provider || '').toLowerCase();
    let adapter = NoopAdapter;
    try {
      if (provider === 'posthog' && config.key) {
        const mod = await import('./analytics/PostHogAdapter');
        adapter = mod.default;
      } else if (provider === 'console') {
        const mod = await import('./analytics/ConsoleAdapter');
        adapter = mod.default;
      }
    } catch (err) {
      console.warn('[analytics] adapter load failed; using no-op', err);
      adapter = NoopAdapter;
    }

    this.adapter = adapter;
    try {
      await adapter.init(config);
    } catch (err) {
      console.warn('[analytics] adapter init failed; falling back to no-op', err);
      this.adapter = NoopAdapter;
    }
  }

  identify(userId, traits = {}) {
    try {
      this.adapter.identify(userId, traits);
    } catch (err) {
      console.warn('[analytics] identify failed', err);
    }
  }

  track(eventName, properties = {}) {
    if (!eventName) return;
    const safe = filterProperties(properties);
    if (this.sessionId && !safe.scan_session_id) {
      safe.scan_session_id = this.sessionId;
    }
    try {
      this.adapter.track(eventName, safe);
    } catch (err) {
      console.warn('[analytics] track failed', err);
    }
  }

  reset() {
    this.sessionId = null;
    try {
      this.adapter.reset();
    } catch (err) {
      console.warn('[analytics] reset failed', err);
    }
  }

  startSession() {
    this.sessionId = generateUuid();
    return this.sessionId;
  }

  getSessionId() {
    return this.sessionId;
  }

  // Test seam: lets unit tests inject an adapter without going through env.
  _setAdapterForTesting(adapter) {
    this.adapter = adapter;
    this.initialized = true;
  }

  // Test seam: full reset for tests that need to exercise init() itself.
  // Resets the singleton state so a fresh init() will run instead of
  // short-circuiting on the `initialized` guard. Production code must not
  // call this — the leading underscore + `_for_testing` suffix marks the
  // intent.
  _resetForTesting() {
    this.adapter = NoopAdapter;
    this.initialized = false;
    this.sessionId = null;
  }
}

const analytics = new AnalyticsService();

export default analytics;
export { ALLOWED_PROPERTY_KEYS, filterProperties, generateUuid };
